import { useAtsEmployerApi } from '@/composables/useApi';
import { InternalError } from '../shared/errors/internal.error';
import MeService from '../shared/me/me.service';

export const ReportType = {
  STANDARD: 'STANDARD',
  RELOCATION: 'RELOCATION',
} as const;

export type LaborMarketReportType = (typeof ReportType)[keyof typeof ReportType];

export enum LocationScope {
  METRO = 'metro',
  STATE = 'state',
  NATION = 'nation',
}

export interface LaborMarketReportRequestInput {
  jobTitle: string;
  location: string;
  reportType: LaborMarketReportType;
  searchRadius?: number; // in miles, only for STANDARD
}

export interface LaborMarketReportRequestOutput {
  success: boolean;
  referenceId: string;
  message: string;
}

export interface ApplicantFlowInsightsRequestInput {
  jobId: number;
  jobTitle: string;
  location: string;
}

export interface ApplicantFlowInsightsRequestOutput {
  success: boolean;
  referenceId: string;
  message: string;
}

export interface GoldUpgradeRequestOutput {
  success: boolean;
  referenceId: string;
  message: string;
}

export interface MarketableCandidateReportFromUserInput {
  batchId: string;
  recruiterEmail: string;
  recruiterFirstName: string;
  candidateUserProfileId: number;
}

export interface MarketableCandidateReportWithResumeInput {
  batchId: string;
  recruiterEmail: string;
  recruiterFirstName: string;
  resumeBase64: string;
  resumeFileName?: string;
}

export interface MarketableCandidateReportResponse {
  success: boolean;
  referenceId: string;
  message: string;
}

export class CustomerSuccessPersistence {
  private meService = new MeService();

  /**
   * Submits a request for a labor market report
   * @param request The labor market report request data
   * @returns The response from the API
   */
  public async submitLaborMarketReport(
    request: LaborMarketReportRequestInput,
  ): Promise<LaborMarketReportRequestOutput | null> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID found', {
        error: 'Missing employer ID',
        status: 400,
        data: { request },
      });
    }

    const url = `/customer-success/labor-market-report/${employerId}`;
    const { data, error, statusCode } = await useAtsEmployerApi(url)
      .post(request)
      .json<LaborMarketReportRequestOutput>();

    if (error.value) {
      throw new InternalError('Failed to submit labor market report', {
        error: error.value,
        status: statusCode.value,
        data: { request, employerId },
      });
    }

    return data.value;
  }

  /**
   * Submits a request for applicant flow insights
   */
  public async submitApplicantFlowInsights(
    request: ApplicantFlowInsightsRequestInput,
  ): Promise<ApplicantFlowInsightsRequestOutput | null> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID found', {
        error: 'Missing employer ID',
        status: 400,
        data: { request },
      });
    }

    const url = `/customer-success/applicant-flow-insights/${employerId}`;
    const { data, error, statusCode } = await useAtsEmployerApi(url)
      .post(request)
      .json<ApplicantFlowInsightsRequestOutput>();

    if (error.value) {
      throw new InternalError('Failed to submit applicant flow insights request', {
        error: error.value,
        status: statusCode.value,
        data: { request, employerId },
      });
    }

    return data.value;
  }

  public async submitMarketableCandidateReportFromUser(
    input: MarketableCandidateReportFromUserInput,
  ): Promise<MarketableCandidateReportResponse | null> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID found', {
        error: 'Missing employer ID',
        status: 400,
        data: { input },
      });
    }

    const url = `/customer-success/marketable-candidate-report/${employerId}/submit-from-user`;
    const { data, error, statusCode } = await useAtsEmployerApi(url)
      .post(input)
      .json<MarketableCandidateReportResponse>();

    if (error.value) {
      throw new InternalError('Failed to submit marketable candidate report', {
        error: error.value,
        status: statusCode.value,
        data: { input, employerId },
      });
    }

    return data.value;
  }

  public async submitMarketableCandidateReportWithResume(
    input: MarketableCandidateReportWithResumeInput,
  ): Promise<MarketableCandidateReportResponse | null> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID found', {
        error: 'Missing employer ID',
        status: 400,
        data: { input: { ...input, resumeBase64: '[redacted]' } },
      });
    }

    const url = `/customer-success/marketable-candidate-report/${employerId}/submit-with-resume`;
    const { data, error, statusCode } = await useAtsEmployerApi(url)
      .post(input)
      .json<MarketableCandidateReportResponse>();

    if (error.value) {
      throw new InternalError('Failed to submit marketable candidate report', {
        error: error.value,
        status: statusCode.value,
        data: { input: { ...input, resumeBase64: '[redacted]' }, employerId },
      });
    }

    return data.value;
  }

  /**
   * Submits a request for gold upgrade
   */
  public async submitGoldUpgradeRequest(): Promise<GoldUpgradeRequestOutput | null> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID found', {
        error: 'Missing employer ID',
        status: 400,
      });
    }

    const url = `/customer-success/gold-upgrade-request/${employerId}`;
    const { data, error, statusCode } = await useAtsEmployerApi(url)
      .post()
      .json<GoldUpgradeRequestOutput>();

    if (error.value) {
      throw new InternalError('Failed to submit gold upgrade request', {
        error: error.value,
        status: statusCode.value,
        data: { employerId },
      });
    }

    return data.value;
  }
}
